'use client';

import React, { createContext, useContext } from 'react';
import { UniversalPaymentStatusEnum } from 'typescript-node-phoenix-payments/dist/model/universalPaymentStatusEnum';
import { OrderReferenceProvider } from '@/ui/providers/order-reference-provider';

/** A single address (shipping or billing), shaped for the reused account OrderAddress component. */
export interface OrderReceivedAddress {
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  taxNumber?: string | null;
  street?: string | null;
  street2?: string | null;
  streetNumber?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
}

/** Order data made available to the modular order-received tiles so they can render real content. */
export interface OrderReceivedData {
  orderReference: string;
  orderId: string;
  isVirtual: boolean;
  paymentStatus: UniversalPaymentStatusEnum | null;
  /** The server-rendered order-summary node (OrderConfirmationSummary), rendered by the order-summary tile. */
  summaryContent?: React.ReactNode;
  /** Shipping + billing addresses for the delivery-info tile. */
  shippingAddress?: OrderReceivedAddress;
  billingAddress?: OrderReceivedAddress;
  /** Tracking stepper state for the delivery-tracking tile. */
  trackingStatus?: string;
  deliveredAsShipped?: boolean;
  /** Pre-rendered PaymentFailed node the payment-status tile drops in on failure (slot pattern). */
  paymentFailedContent?: React.ReactNode;
}

const OrderReceivedContext = createContext<OrderReceivedData | undefined>(undefined);

/** Returns `undefined` outside the provider so tiles stay reusable on non-order pages. */
export function useOrderReceivedData(): OrderReceivedData | undefined {
  return useContext(OrderReceivedContext);
}

interface OrderReceivedDataProviderProps {
  value: OrderReceivedData;
  children: React.ReactNode;
}

export function OrderReceivedDataProvider({
  value,
  children,
}: Readonly<OrderReceivedDataProviderProps>) {
  return (
    <OrderReceivedContext.Provider value={value}>
      <OrderReferenceProvider orderReference={value.orderReference}>
        {children}
      </OrderReferenceProvider>
    </OrderReceivedContext.Provider>
  );
}
